/**
 * The arranger's choices, each off until it is asked for.
 *
 * Arranging a score hands the players its own notes and nothing else; anything beyond that is
 * an arranger's choice and is made only when asked for by name. Each box here is one such
 * choice, the Harmony menu says how far the harmony may go beyond what is written, the Texture
 * menu how the notes it adds are played, the Counter menu whether a player given nothing
 * plays a counter-melody, and the Tune menu how far the tune is varied; each is sent with the run as the field of the same name, and the
 * reading bar says what each one did.
 * What a control does is said in its title, as every control on the toolbar says it.
 * Starting from the tune shuts every one of them (`shut`), since they shape the score's own
 * notes and none are kept; what was chosen stays set for when the score is started from again.
 */

import {
  COUNTERS,
  HARMONY_LEVELS,
  TEXTURES,
  VARIATIONS,
  type ArrangeRequest,
  type Counter,
  type HarmonyLevel,
  type Texture,
  type Variation,
} from "@app/shared-types";
import ArrangeMenu, { type Named } from "./ArrangeMenu";
import { LABEL } from "./Chrome";

/** Which choices are asked for. */
export type Choices = Required<
  Pick<
    ArrangeRequest,
    | "harmony"
    | "texture"
    | "counter"
    | "vary"
    | "follow"
    | "rise"
    | "moving"
    | "stops"
    | "double"
    | "share"
  >
>;

/** Nothing asked for: the source's notes as written. */
export const NO_CHOICES: Choices = {
  harmony: "written",
  texture: "held",
  counter: "off",
  vary: "off",
  follow: false,
  rise: false,
  moving: false,
  stops: false,
  double: false,
  share: false,
};

/** The choices that are on or off. */
export type Box = Exclude<keyof Choices, "harmony" | "texture" | "counter" | "vary">;

/** Each harmony level as the menu names it. Keyed by level so a new one cannot go unnamed. */
const LEVELS: Named<HarmonyLevel> = {
  written: {
    label: "As written",
    title: "The source's notes and nothing else: a player the music gives nothing to rests.",
  },
  fill: {
    label: "Fill in",
    title:
      "Players with nothing to play are given the notes of the chord that nobody sounds: the chord the source's chord symbols name where it prints them, otherwise the one the bass and the tune imply by the rule of the octave in the piece's key, chosen once a beat and again where a chord symbol changes, each lasting to the next. Under a tune sounding alone for a beat or more, a chord symbol's whole chord is given, bass and all, below the tune. The source's own notes stay as written, and the notes added are counted apart from them.",
  },
  complete: {
    label: "Complete",
    title:
      "Everything Fill in does, and where the source names no chord -- a tune sung alone, or a lead sheet before its first symbol -- a chord is chosen for the tune, a bar at a time (half a bar in four): from the key's own triads and its dominant seventh, holding the tune's note on the strong beat, moving as chords usually move, opening and closing on the tonic. The chosen chord is played, bass and all, under the tune, over a bass that moves by step where it can, wherever it sounds alone. The source's own chord symbols are kept wherever they are in force.",
  },
  free: {
    label: "Free",
    title:
      "Keeps only the tune -- taken from the next staff down wherever the top staff rests -- and leaves the rest of the source out: its accompaniment, its bass and its chord symbols. Chords are chosen for the tune a bar at a time (half a bar in four) from the key's own triads, its dominant seventh and the dominant seventh of each of its other major and minor triads, and played, bass and all, by the players left, over a bass that moves by step where it can. The notes left out are counted.",
  },
};

/** Each texture as the menu names it. Keyed by texture so a new one cannot go unnamed. */
const TEXTURE_NAMES: Named<Texture> = {
  held: {
    label: "Held",
    title: "Each note the harmony adds is held for as long as its chord lasts.",
  },
  repeated: {
    label: "Repeated",
    title:
      "Each note the harmony adds is struck again on every beat inside its chord -- a dotted beat in a compound metre -- counted from the bar line. A note shorter than the beat stays one note. The source's own notes are not touched.",
  },
  broken: {
    label: "Broken",
    title:
      "In each chord the harmony adds, one player plays its notes one after another, two to a beat (three in a compound metre), rising through the chord and falling back where it runs out of room, while the others hold. The player walking stays above the chord's lowest note and below the tune and every other note of the source's. The source's own notes are not touched.",
  },
  alberti: {
    label: "Alberti",
    title:
      "In each chord the harmony adds, one player plays three neighbouring chord notes within an octave -- lowest, highest, middle, highest -- over and over at the beat's division (two where only two fit), while the others hold. It stays above the chord's lowest note and below the tune. Another take may choose a different figure. The source's own notes are not touched.",
  },
  waltz: {
    label: "Waltz",
    title:
      "The lowest note the harmony adds is played on the bar's first beat and the other notes it adds on the beats after -- oom, pah, pah. A note no beat line crosses stays as it was. The source's own notes are not touched.",
  },
  march: {
    label: "March",
    title:
      "The lowest note the harmony adds is played on the bar's first beat and every other beat after it, and the other notes it adds on the beats between -- oom, pah. A note no beat line crosses stays as it was. The source's own notes are not touched.",
  },
  syncopated: {
    label: "Syncopated",
    title:
      "The notes the harmony adds above its lowest are struck just after each beat -- half a beat late, a third of one in a compound metre -- and held across the next beat, while the lowest holds. A note no beat line crosses stays as it was. The source's own notes are not touched.",
  },
};

/** Each counter-melody as the menu names it. Keyed so a new one cannot go unnamed. */
const COUNTER_NAMES: Named<Counter> = {
  off: {
    label: "None",
    title:
      "No counter-melody: a player the music gives nothing to rests, or plays what the harmony adds.",
  },
  plain: {
    label: "Plain",
    title:
      "The highest player the music gives nothing to plays a counter-melody of the arranger's own against the tune: a note of the chord on each beat, moving against the tune where it can, below it and above the bass, within the player's compass and never in parallel fifths or octaves with another part. Where every player has notes there is nobody to play it. Another take may choose another line. The source's own notes are not touched, and the new ones are counted apart.",
  },
  flowing: {
    label: "Flowing",
    title:
      "As Plain, with passing notes filling the line's leaps and neighbour notes turning its repeated notes between the beats -- about two notes to a beat (three in a compound metre). The source's own notes are not touched, and the new ones are counted apart.",
  },
};

/** Each variation of the tune as the menu names it. Keyed so a new one cannot go unnamed. */
const TUNE_NAMES: Named<Variation> = {
  off: {
    label: "As written",
    title: "The tune is played as the source writes it.",
  },
  light: {
    label: "Light",
    title:
      "The tune is varied with passing notes of the key's scale across its leaps of a third or a fourth, at the end of a note that moves straight on to the next, which is cut short to make room. Only where they stay above every other part and move in no parallel fifths or octaves with one. The new notes are counted apart from the source's.",
  },
  more: {
    label: "More",
    title:
      "As Light, and a repeated note of the tune is turned into a neighbour note and back, and a note two beats long or more that moves on by step ends with a turn: the step above, the note, the step below, the note. Another take may choose the neighbour below, or the turn the other way round.",
  },
  free: {
    label: "Free",
    title:
      "As More, and the tune's leaps wider than a fourth, up to an octave, are filled with runs of the key's scale.",
  },
};

/** Why the Texture menu is shut, where it is: As written adds no notes to play otherwise. */
const NOTHING_ADDED = "As written adds no notes, so there is nothing for a texture to play.";

const BOXES: ReadonlyArray<{ key: Box; label: string; title: string }> = [
  {
    key: "follow",
    label: "Follow the tune down",
    title:
      "While the top staff rests, the tune is taken from the next staff down that sounds, instead of leaving the tune's player waiting. Changes only a score handed out line by line, not one whose staves each go whole to a player.",
  },
  {
    key: "rise",
    label: "Follow the bass up",
    title:
      "While the lowest staff rests, the bass is taken from the next staff up that sounds, instead of leaving the bass's player waiting. Changes only a score handed out line by line, not one whose staves each go whole to a player.",
  },
  {
    key: "moving",
    label: "Moving lines first",
    title:
      "Where there are more notes between the tune and the bass than players, a moving line's short notes are given a player before a long held note is. Changes only a score handed out line by line, not one whose staves each go whole to a player, and nothing on a roster with a player of two hands, whose hands play every note no player is cast for.",
  },
  {
    key: "stops",
    label: "Double stops",
    title:
      "Where there are more notes between the tune and the bass than players, a note no player is free for is given to a string player as the second note of a double stop: struck with the player's own note, on the next string, within the hand's reach, and nowhere it would cross another player's note. Changes only a score handed out line by line, not one whose staves each go whole to a player, and nothing on a roster with a player of two hands.",
  },
  {
    key: "double",
    label: "Double the lines",
    title:
      "Players given nothing double a line for the whole passage: the tune in unison or an octave below, the bass in unison or an octave below, an inner line in unison, each on the player whose best register lies nearest it. The first player on a line doubles only its notes marked mf or louder, any further player only those marked f or louder, since doubling belongs to loud passages. The source's own notes stay as written, and the notes written a second time are counted apart from them. For a player of two hands alone, the tune and the bass are played in octaves instead: each doubled an octave below in its own hand, where the hand can reach it. Beside one, the players given nothing double as they do on any roster, and its hands play no octaves. With Share the lines the players double by turns instead, as that box says.",
  },
  {
    key: "share",
    label: "Share the lines",
    title:
      "Players given nothing, and players whose own line rests through more than half the passage, share the tune, the bass and the inner lines by turns: a line's players lead it one after another, a phrase of four bars at a time, begun at the note of the tune nearest the bar line that follows its longest wait. The notes between the tune and the bass are first gathered on as few players as can play them, so that more are free to. Each waiting player is given the line by which it comes to play the most notes, so a long tune is shared by many and a short inner line stays its own player's. No note is added, moved or left out. A player never takes the tune in a phrase where one of its family seated higher sounds it an octave below. With Double the lines, a note marked mf or louder is offered the next player in turn as well and one marked f or louder all of a line's players, the tune and the bass in unison or an octave below, each playing it last of all where it is still silent and, with the Check, where it adds no fault; without it every note is one player's alone. Nothing for a player of two hands alone, and not with an AI arranger's plan of who rests.",
  },
];

/** What a choice is called on its box, so a control elsewhere that ticks it says the same. */
export function choiceLabel(key: Box): string {
  return BOXES.find((box) => box.key === key)?.label ?? key;
}

/** What a harmony level is called on its menu. */
export function levelLabel(level: HarmonyLevel): string {
  return LEVELS[level].label;
}

/** What a counter-melody is called on its menu. */
export function counterLabel(counter: Counter): string {
  return COUNTER_NAMES[counter].label;
}

/** What a variation of the tune is called on its menu. */
export function varyLabel(vary: Variation): string {
  return TUNE_NAMES[vary].label;
}

/** What a texture is called on its menu. */
export function textureLabel(texture: Texture): string {
  return TEXTURE_NAMES[texture].label;
}

export default function ArrangeChoices({
  choices,
  shut,
  onChoices,
}: {
  readonly choices: Choices;
  /** Why every choice is shut, where they are; each control's title then says so. */
  readonly shut?: string;
  onChoices(choices: Choices): void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <ArrangeMenu
        label="Harmony"
        value={choices.harmony}
        values={HARMONY_LEVELS}
        names={LEVELS}
        shut={shut}
        onPick={(harmony) => onChoices({ ...choices, harmony })}
      />
      <ArrangeMenu
        label="Texture"
        value={choices.texture}
        values={TEXTURES}
        names={TEXTURE_NAMES}
        shut={shut ?? (choices.harmony === "written" ? NOTHING_ADDED : undefined)}
        onPick={(texture) => onChoices({ ...choices, texture })}
      />
      <ArrangeMenu
        label="Counter"
        value={choices.counter}
        values={COUNTERS}
        names={COUNTER_NAMES}
        shut={shut}
        onPick={(counter) => onChoices({ ...choices, counter })}
      />
      <ArrangeMenu
        label="Tune"
        value={choices.vary}
        values={VARIATIONS}
        names={TUNE_NAMES}
        shut={shut}
        onPick={(vary) => onChoices({ ...choices, vary })}
      />
      <span className={LABEL}>Options</span>
      {BOXES.map((box) => (
        <label
          key={box.key}
          title={shut ?? box.title}
          className={`inline-flex h-7 items-center gap-1.5 text-[11px] text-shell-text ${shut === undefined ? "cursor-pointer hover:text-shell-lit" : "opacity-40"}`}
        >
          <input
            type="checkbox"
            checked={choices[box.key]}
            disabled={shut !== undefined}
            onChange={(event) => onChoices({ ...choices, [box.key]: event.target.checked })}
            className="h-3.5 w-3.5 accent-shell-live"
          />
          {box.label}
        </label>
      ))}
    </div>
  );
}
